/**
 * Sticker Registry
 * 
 * Centralized registry of all bundled sticker packs in the app.
 * These stickers are included in the app bundle (not downloaded from server).
 * 
 * HOW TO ADD NEW STICKERS:
 * 1. Add your .png file to /assets/stickers/{category}/
 * 2. Add an entry to the appropriate pack in STICKER_PACKS below
 * 3. The sticker will be available in the Avatar Studio
 */

/**
 * Sticker pack interface
 */
export interface StickerPack {
  /** Unique identifier matching database slug */
  slug: string;
  /** Display name (Dutch) */
  name: string;
  /** Description */
  description: string;
  /** Ionicons icon name */
  icon: string;
  /** Theme color */
  color: string;
  /** Array of stickers in this pack */
  stickers: Sticker[];
}

/**
 * Individual sticker interface
 */
export interface Sticker {
  /** Unique identifier matching database slug */
  slug: string;
  /** Display name (Dutch) */
  name: string;
  /** The require() source for Image component */
  source: any;
}

/**
 * All bundled sticker packs
 */
export const STICKER_PACKS: StickerPack[] = [
  {
    slug: "animals",
    name: "Dieren",
    description: "Schattige dieren stickers",
    icon: "paw",
    color: "#10B981",
    stickers: [
      { slug: "bear", name: "Beer", source: require("@/assets/stickers/animals_stickers/bear.png") },
      { slug: "crab", name: "Krab", source: require("@/assets/stickers/animals_stickers/crab.png") },
      { slug: "crocodile", name: "Krokodil", source: require("@/assets/stickers/animals_stickers/crocodile.png") },
      { slug: "cute-animals", name: "Schattige Dieren", source: require("@/assets/stickers/animals_stickers/cute-animals.png") },
      { slug: "dinosaur", name: "Dinosaurus", source: require("@/assets/stickers/animals_stickers/dinosaur.png") },
      { slug: "elk", name: "Eland", source: require("@/assets/stickers/animals_stickers/elk.png") },
      { slug: "fox", name: "Vos", source: require("@/assets/stickers/animals_stickers/fox.png") },
      { slug: "hedgehog", name: "Egel", source: require("@/assets/stickers/animals_stickers/hedgehog.png") },
      { slug: "jellyfish", name: "Kwal", source: require("@/assets/stickers/animals_stickers/jellyfish.png") },
      { slug: "lion", name: "Leeuw", source: require("@/assets/stickers/animals_stickers/lion.png") },
      { slug: "penguin", name: "Pinguïn", source: require("@/assets/stickers/animals_stickers/penguin.png") },
      { slug: "polar-bear", name: "IJsbeer", source: require("@/assets/stickers/animals_stickers/polar-bear.png") },
      { slug: "rabbit", name: "Konijn", source: require("@/assets/stickers/animals_stickers/rabbit.png") },
      { slug: "raccoon", name: "Wasbeer", source: require("@/assets/stickers/animals_stickers/raccoon.png") },
      { slug: "shrimp", name: "Garnaal", source: require("@/assets/stickers/animals_stickers/shrimp.png") },
      { slug: "squirrel", name: "Eekhoorn", source: require("@/assets/stickers/animals_stickers/squirrel.png") },
      { slug: "the-cow", name: "Koe", source: require("@/assets/stickers/animals_stickers/the-cow.png") },
      { slug: "turtle", name: "Schildpad", source: require("@/assets/stickers/animals_stickers/turtle.png") },
      { slug: "whale", name: "Walvis", source: require("@/assets/stickers/animals_stickers/whale.png") },
    ],
  },
  {
    slug: "fitness_food",
    name: "Fitness & Eten",
    description: "Sport en voedsel stickers",
    icon: "fitness",
    color: "#F59E0B",
    stickers: [
      { slug: "apple", name: "Appel", source: require("@/assets/stickers/fitness_and_food/apple.png") },
      { slug: "avocado", name: "Avocado", source: require("@/assets/stickers/fitness_and_food/avocado.png") },
      { slug: "banana", name: "Banaan", source: require("@/assets/stickers/fitness_and_food/banana.png") },
      { slug: "boxing", name: "Boksen", source: require("@/assets/stickers/fitness_and_food/boxing.png") },
      { slug: "cherry", name: "Kers", source: require("@/assets/stickers/fitness_and_food/cherry.png") },
      { slug: "coffee", name: "Koffie", source: require("@/assets/stickers/fitness_and_food/coffee.png") },
      { slug: "dumbbel", name: "Dumbbell", source: require("@/assets/stickers/fitness_and_food/dumbbel.png") },
      { slug: "fish", name: "Vis", source: require("@/assets/stickers/fitness_and_food/fish.png") },
      { slug: "grape", name: "Druif", source: require("@/assets/stickers/fitness_and_food/grape.png") },
      { slug: "kiwi-fruit", name: "Kiwi", source: require("@/assets/stickers/fitness_and_food/kiwi-fruit.png") },
      { slug: "lemon", name: "Citroen", source: require("@/assets/stickers/fitness_and_food/lemon.png") },
      { slug: "milk", name: "Melk", source: require("@/assets/stickers/fitness_and_food/milk.png") },
      { slug: "peach", name: "Perzik", source: require("@/assets/stickers/fitness_and_food/peach.png") },
      { slug: "poached-eggs", name: "Gepocheerde Eieren", source: require("@/assets/stickers/fitness_and_food/poached-eggs.png") },
      { slug: "running", name: "Hardlopen", source: require("@/assets/stickers/fitness_and_food/running.png") },
      { slug: "soda-water", name: "Frisdrank", source: require("@/assets/stickers/fitness_and_food/soda-water.png") },
      { slug: "steak", name: "Steak", source: require("@/assets/stickers/fitness_and_food/steak.png") },
      { slug: "watermelon", name: "Watermeloen", source: require("@/assets/stickers/fitness_and_food/watermelon.png") },
      { slug: "weighing-scale", name: "Weegschaal", source: require("@/assets/stickers/fitness_and_food/weighing-scale.png") },
      { slug: "yogurt", name: "Yoghurt", source: require("@/assets/stickers/fitness_and_food/yogurt.png") },
    ],
  },
  {
    slug: "travel",
    name: "Reizen",
    description: "Vakantie en reizen stickers",
    icon: "airplane",
    color: "#3B82F6",
    stickers: [
      { slug: "cocktail", name: "Cocktail", source: require("@/assets/stickers/travel/cocktail.png") },
      { slug: "coconut-tree", name: "Palmboom", source: require("@/assets/stickers/travel/coconut-tree.png") },
      { slug: "emu", name: "Emoe", source: require("@/assets/stickers/travel/emu.png") },
      { slug: "hotel", name: "Hotel", source: require("@/assets/stickers/travel/hotel.png") },
      { slug: "ice-cream", name: "IJsje", source: require("@/assets/stickers/travel/ice-cream.png") },
      { slug: "indicator", name: "Wegwijzer", source: require("@/assets/stickers/travel/indicator.png") },
      { slug: "lighthouse", name: "Vuurtoren", source: require("@/assets/stickers/travel/lighthouse.png") },
      { slug: "map", name: "Kaart", source: require("@/assets/stickers/travel/map.png") },
      { slug: "milk-tea", name: "Bubble Tea", source: require("@/assets/stickers/travel/milk-tea.png") },
      { slug: "polaroid", name: "Polaroid", source: require("@/assets/stickers/travel/polaroid.png") },
      { slug: "snow-mountain", name: "Sneeuwberg", source: require("@/assets/stickers/travel/snow-mountain.png") },
      { slug: "suitcase", name: "Koffer", source: require("@/assets/stickers/travel/suitcase.png") },
      { slug: "sunbathing", name: "Zonnen", source: require("@/assets/stickers/travel/sunbathing.png") },
      { slug: "surf", name: "Surfen", source: require("@/assets/stickers/travel/surf.png") },
      { slug: "suv", name: "SUV", source: require("@/assets/stickers/travel/suv.png") },
      { slug: "take-a-bath", name: "Bad", source: require("@/assets/stickers/travel/take-a-bath.png") },
      { slug: "wallet", name: "Portemonnee", source: require("@/assets/stickers/travel/wallet.png") },
      { slug: "wine", name: "Wijn", source: require("@/assets/stickers/travel/wine.png") },
    ],
  },
];

/**
 * Get all sticker packs
 */
export const getAllStickerPacks = (): StickerPack[] => STICKER_PACKS;

/**
 * Get a single sticker pack by slug
 */
export const getStickerPackBySlug = (slug: string): StickerPack | undefined =>
  STICKER_PACKS.find((pack) => pack.slug === slug);

/**
 * Get all stickers from all packs (flattened)
 */
export const getAllStickers = (): Sticker[] =>
  STICKER_PACKS.flatMap((pack) => pack.stickers);

/**
 * Get a single sticker by slug (searches all packs)
 */
export const getStickerBySlug = (slug: string): Sticker | undefined =>
  getAllStickers().find((sticker) => sticker.slug === slug);

/**
 * Get sticker source by slug (for Image component)
 */
export const getStickerSource = (slug: string): any | undefined =>
  getStickerBySlug(slug)?.source;

/**
 * Get total sticker count
 */
export const getTotalStickerCount = (): number =>
  STICKER_PACKS.reduce((total, pack) => total + pack.stickers.length, 0);

